import { z } from 'zod';
/** Umowy, szablony dokumentów, płatności i warunki startu (docs/02 rozdz. 5a, 5.5, 5.6, 7, 11). */

export const TEMPLATE_KINDS = { umowa: 'Umowa', aneks: 'Aneks', oferta: 'Oferta', protokol: 'Protokół' } as const;
export type TemplateKind = keyof typeof TEMPLATE_KINDS;

export const CONTRACT_KINDS = { umowa: 'Umowa', aneks: 'Aneks' } as const;

/**
 * Podstawa realizacji: umowa (dokument do podpisu) albo zlecenie na podstawie zaakceptowanej oferty — szybka ścieżka
 * dla spraw poniżej progu wartości (ustawienia firmy). Zlecenie powstaje samo przy akceptacji i od razu pozwala
 * planować; harmonogram: jedna płatność po odbiorze, faktura po protokole odbioru.
 */
export const CONTRACT_BASES = { umowa: 'Umowa', oferta: 'Zlecenie wg oferty' } as const;
export type ContractBasis = keyof typeof CONTRACT_BASES;
export type ContractKind = keyof typeof CONTRACT_KINDS;

export const CONTRACT_STATUSES = {
  roboczy: 'Robocza',
  wydany: 'Wydana do podpisu',
  podpisany: 'Podpisana',
  anulowany: 'Anulowana',
} as const;
export type ContractStatus = keyof typeof CONTRACT_STATUSES;

export const PAYMENT_METHODS = { przelew: 'Przelew', gotowka: 'Gotówka', karta: 'Karta', dotacja: 'Dotacja', inne: 'Inne' } as const;
export type PaymentMethod = keyof typeof PAYMENT_METHODS;
/** Sposób zapisanej wpłaty: ręczne (PAYMENT_METHODS) i „online” — płatność przez bramkę ING Pay, nie do wyboru ręcznie. */
export const PAYMENT_METHOD_LABELS = { ...PAYMENT_METHODS, online: 'Online (ING Pay)' } as const;
export type RecordedPaymentMethod = keyof typeof PAYMENT_METHOD_LABELS;

export const PAYMENT_ITEM_STATUSES = { oczekuje: 'Oczekuje', czesciowo: 'Częściowo opłacona', oplacona: 'Opłacona', anulowana: 'Anulowana' } as const;
export type PaymentItemStatus = keyof typeof PAYMENT_ITEM_STATUSES;

/** Stan realizacji zakresu (docs/02 5.2) — w P3 do etapu „do zaplanowania”; dalsze stany od P4. */
export const EXECUTION_STATES = {
  nie_dotyczy: 'Nie dotyczy (przed umową)',
  oczekuje_na_warunki: 'Oczekuje na warunki',
  do_zaplanowania: 'Do zaplanowania',
  zaplanowany: 'Zaplanowany',
  zaplanowany_warunkowo: 'Zaplanowany warunkowo',
  w_realizacji: 'W realizacji',
  wstrzymany: 'Wstrzymany',
  do_odbioru: 'Do odbioru',
  odebrany: 'Odebrany',
} as const;
export type ExecutionState = keyof typeof EXECUTION_STATES;

export interface TemplateField {
  key: string;
  label: string;
  sample: string;
}

/** Pola dostępne w szablonach umów i aneksów (lista dla edytora + dane przykładowe do podglądu). */
export const TEMPLATE_FIELD_GROUPS: { label: string; fields: TemplateField[] }[] = [
  {
    label: 'Firma (wystawca)',
    fields: [
      { key: 'firma.nazwa', label: 'Pełna nazwa', sample: 'Aurora Energia spółka z ograniczoną odpowiedzialnością' },
      { key: 'firma.skrot', label: 'Nazwa skrócona', sample: 'Aurora Energia sp. z o.o.' },
      { key: 'firma.adres', label: 'Adres', sample: 'ul. Wiatrowa 3, 44-100 Gliwice' },
      { key: 'firma.nip', label: 'NIP', sample: '0000000000' },
      { key: 'firma.regon', label: 'REGON', sample: '000000000' },
      { key: 'firma.krs', label: 'KRS', sample: '0000000000' },
      { key: 'firma.rachunek', label: 'Rachunek bankowy', sample: '00 0000 0000 0000 0000 0000 0000' },
      { key: 'firma.email', label: 'E-mail', sample: 'biuro@firma.example' },
      { key: 'firma.telefon', label: 'Telefon', sample: '32 000 00 00' },
    ],
  },
  {
    label: 'Klient i płatnik',
    fields: [
      { key: 'klient.nazwa', label: 'Klient — nazwa', sample: 'Marek Zieliński' },
      { key: 'klient.adres', label: 'Klient — adres', sample: 'ul. Lipowa 12, 43-190 Mikołów' },
      { key: 'klient.nip', label: 'Klient — NIP', sample: '' },
      { key: 'klient.telefon', label: 'Klient — telefon', sample: '600 100 200' },
      { key: 'klient.email', label: 'Klient — e-mail', sample: 'klient@poczta.example' },
      { key: 'platnik.nazwa', label: 'Płatnik — nazwa', sample: 'Marek Zieliński' },
      { key: 'platnik.adres', label: 'Płatnik — adres', sample: 'ul. Lipowa 12, 43-190 Mikołów' },
      { key: 'platnik.nip', label: 'Płatnik — NIP', sample: '' },
    ],
  },
  {
    label: 'Obiekt i zakres',
    fields: [
      { key: 'lokalizacja.adres', label: 'Adres realizacji', sample: 'ul. Lipowa 12, 43-190 Mikołów' },
      { key: 'zakresy.nazwy', label: 'Branże w umowie', sample: 'Pompa ciepła' },
      { key: 'oferta.numery', label: 'Numery zaakceptowanych ofert', sample: 'OF/B/2026/0001' },
      { key: 'sprawa.numer', label: 'Numer sprawy', sample: 'S/2026/0001' },
      { key: 'opiekun.nazwa', label: 'Opiekun (handlowiec)', sample: 'Karolina Wójcik' },
    ],
  },
  {
    label: 'Umowa',
    fields: [
      { key: 'umowa.numer', label: 'Numer umowy', sample: 'UM/B/2026/0001' },
      { key: 'umowa.data', label: 'Data wydania', sample: '24 października 2026' },
      { key: 'umowa.miejscowosc', label: 'Miejscowość (siedziba firmy)', sample: 'Gliwice' },
      { key: 'umowa.termin_realizacji', label: 'Termin realizacji', sample: 'do 6 tygodni od podpisania umowy' },
      { key: 'umowa.wartosc_netto', label: 'Wartość netto', sample: '42 560,00 zł' },
      { key: 'umowa.wartosc_vat', label: 'Kwota VAT', sample: '3 404,80 zł' },
      { key: 'umowa.wartosc_brutto', label: 'Wartość brutto', sample: '45 964,80 zł' },
      { key: 'umowa.wartosc_brutto_slownie', label: 'Wartość brutto słownie', sample: 'czterdzieści pięć tysięcy dziewięćset sześćdziesiąt cztery złote 80/100' },
      { key: 'umowa.zaliczka', label: 'Wymagana zaliczka (warunek)', sample: 'tak' },
      { key: 'umowa.zaliczka_kwota', label: 'Kwota zaliczki', sample: '13 789,44 zł' },
      { key: 'umowa.podpis_elektroniczny', label: 'Wydano do podpisu elektronicznego (warunek)', sample: '' },
      { key: 'aneks.numer', label: 'Numer aneksu', sample: 'UM/B/2026/0001/A1' },
      { key: 'aneks.umowa_numer', label: 'Numer umowy, której dotyczy aneks', sample: 'UM/B/2026/0001' },
      { key: 'aneks.umowa_data', label: 'Data podpisania umowy', sample: '10 listopada 2026' },
      { key: 'aneks.anulowane_platnosci', label: 'Płatności anulowane aneksem', sample: 'Płatność końcowa po odbiorze (28 700,00 zł)' },
    ],
  },
];

export const TEMPLATE_BLOCKS = [
  { key: 'tabela_pozycji', label: 'Tabela: przedmiot umowy z cenami' },
  { key: 'harmonogram_platnosci', label: 'Tabela: harmonogram płatności' },
  { key: 'zalaczniki', label: 'Lista załączników (zaakceptowane oferty — dodawana automatycznie na końcu, jeśli jej nie wstawisz)' },
] as const;

/** Bloki aneksu — te same dane co w umowie, ale tylko pozycje i płatności wprowadzane aneksem (aneks nie ma załączników). */
export const ANNEX_BLOCKS = [
  { key: 'tabela_pozycji', label: 'Tabela: pozycje dodane lub zmienione aneksem, z sumami netto, VAT i brutto' },
  { key: 'harmonogram_platnosci', label: 'Tabela: płatności dodane aneksem' },
] as const;

export const SAMPLE_FIELDS: Record<string, string> = Object.fromEntries(TEMPLATE_FIELD_GROUPS.flatMap((g) => g.fields.map((f) => [f.key, f.sample])));

// ───────────── pola i bloki wg rodzaju szablonu ─────────────

const FIRMA_GROUP = TEMPLATE_FIELD_GROUPS[0]!;

/** Pola szablonu oferty — wyłącznie z niezmiennej migawki wydanej wersji. */
export const OFFER_FIELD_GROUPS: { label: string; fields: TemplateField[] }[] = [
  FIRMA_GROUP,
  {
    label: 'Klient i obiekt',
    fields: [
      { key: 'klient.nazwa', label: 'Klient — nazwa', sample: 'Marek Zieliński' },
      { key: 'klient.adres', label: 'Klient — adres', sample: 'ul. Lipowa 12, 43-190 Mikołów' },
      { key: 'lokalizacja.adres', label: 'Adres realizacji', sample: 'ul. Lipowa 12, 43-190 Mikołów' },
      { key: 'zakresy.nazwy', label: 'Branże w ofercie', sample: 'Pompa ciepła' },
    ],
  },
  {
    label: 'Oferta',
    fields: [
      { key: 'oferta.numer', label: 'Numer oferty', sample: 'OF/B/2026/0001' },
      { key: 'oferta.wersja', label: 'Numer wersji', sample: '1' },
      { key: 'oferta.data', label: 'Data wydania', sample: '24 października 2026' },
      { key: 'oferta.wazna_do', label: 'Ważna do', sample: '23 listopada 2026' },
      { key: 'oferta.tytul', label: 'Tytuł oferty', sample: 'Pompa ciepła powietrze-woda dla domu jednorodzinnego' },
      { key: 'oferta.liczba_wariantow', label: 'Liczba wariantów', sample: '1' },
      { key: 'oferta.wiele_wariantow', label: 'Więcej niż jeden wariant (warunek)', sample: '' },
      { key: 'oferta.warunki', label: 'Warunki (tekst, do warunku)', sample: 'Termin realizacji do 6 tygodni od podpisania umowy.' },
      { key: 'oferta.wartosc_brutto', label: 'Wartość brutto (przy jednym wariancie)', sample: '45 964,80 zł' },
    ],
  },
  {
    label: 'Przygotował(a)',
    fields: [
      { key: 'przygotowal.nazwa', label: 'Imię i nazwisko', sample: 'Karolina Wójcik' },
      { key: 'przygotowal.telefon', label: 'Telefon', sample: '600 000 001' },
      { key: 'przygotowal.email', label: 'E-mail', sample: 'k.wojcik@firma.example' },
    ],
  },
];

/** Pola szablonu protokołu — z podpisanej (niezmiennej) treści protokołu. */
export const PROTOCOL_FIELD_GROUPS: { label: string; fields: TemplateField[] }[] = [
  {
    label: 'Wykonawca',
    fields: [
      { key: 'firma.nazwa', label: 'Nazwa', sample: 'Aurora Energia sp. z o.o.' },
      { key: 'firma.adres', label: 'Adres', sample: 'ul. Wiatrowa 3, 44-100 Gliwice' },
      { key: 'firma.nip', label: 'NIP', sample: '0000000000' },
    ],
  },
  {
    label: 'Klient i obiekt',
    fields: [
      { key: 'klient.nazwa', label: 'Klient — nazwa', sample: 'Marek Zieliński' },
      { key: 'lokalizacja.adres', label: 'Adres', sample: 'ul. Lipowa 12, 43-190 Mikołów' },
      { key: 'zakresy.nazwy', label: 'Branże', sample: 'Pompa ciepła' },
      { key: 'zadanie.numer', label: 'Numer zadania', sample: 'Z/2026/0001' },
    ],
  },
  {
    label: 'Protokół',
    fields: [
      { key: 'protokol.tytul', label: 'Tytuł (rodzaj i branże)', sample: 'Protokół odbioru montażu — Pompa ciepła' },
      { key: 'protokol.rodzaj', label: 'Rodzaj', sample: 'Protokół odbioru montażu' },
      { key: 'protokol.kod', label: 'Identyfikator', sample: 'PO-7F3K92' },
      { key: 'protokol.data_podpisu', label: 'Data i godzina podpisu', sample: '24 października 2026 14:20' },
      { key: 'protokol.podpisal', label: 'Podpisał (klient)', sample: 'Marek Zieliński' },
      { key: 'protokol.wykonal', label: 'Wykonał', sample: 'Tomasz Kruk' },
      { key: 'protokol.uwagi_klienta', label: 'Uwagi klienta (tekst, do warunku)', sample: '' },
      { key: 'protokol.skrot', label: 'Skrót treści SHA-256', sample: '3f2a…' },
    ],
  },
];

export interface TemplateBlockDef {
  key: string;
  label: string;
}

export const OFFER_BLOCKS: TemplateBlockDef[] = [
  { key: 'warianty', label: 'Warianty oferty: pozycje, sumy, VAT (dodawane na końcu, jeśli ich nie wstawisz)' },
  { key: 'warunki', label: 'Warunki oferty (tekst z akapitami)' },
];

export const PROTOCOL_BLOCKS: TemplateBlockDef[] = [
  { key: 'protokol_sekcje', label: 'Treść protokołu: dane, urządzenia, pomiary, czynności, uwagi' },
  { key: 'oswiadczenia', label: 'Oświadczenia z szablonu protokołu' },
  { key: 'podpis', label: 'Podpis klienta, data, wykonawca, skrót treści (dodawany, jeśli go nie wstawisz)' },
  { key: 'zdjecia', label: 'Zdjęcia na kolejnych stronach (dodawane, jeśli ich nie wstawisz)' },
];

/** Grupy pól, bloki i przykładowy warunek dla edytora — wg rodzaju szablonu. */
export const TEMPLATE_SCHEMA: Record<TemplateKind, { groups: { label: string; fields: TemplateField[] }[]; blocks: readonly TemplateBlockDef[]; condition: { label: string; text: string } }> = {
  umowa: { groups: TEMPLATE_FIELD_GROUPS, blocks: TEMPLATE_BLOCKS, condition: { label: 'Jeśli wymagana zaliczka…', text: '{{#if umowa.zaliczka}}Treść, gdy wymagana jest zaliczka.{{/if}}' } },
  aneks: { groups: TEMPLATE_FIELD_GROUPS, blocks: ANNEX_BLOCKS, condition: { label: 'Jeśli aneks anuluje płatności…', text: '{{#if aneks.anulowane_platnosci}}Z harmonogramu usuwa się: {{aneks.anulowane_platnosci}}.{{/if}}' } },
  oferta: { groups: OFFER_FIELD_GROUPS, blocks: OFFER_BLOCKS, condition: { label: 'Jeśli kilka wariantów…', text: '{{#if oferta.wiele_wariantow}}Prosimy o wybór jednego wariantu.{{/if}}' } },
  protokol: { groups: PROTOCOL_FIELD_GROUPS, blocks: PROTOCOL_BLOCKS, condition: { label: 'Jeśli klient zgłosił uwagi…', text: '{{#if protokol.uwagi_klienta}}Klient zgłosił uwagi — zostaną rozpatrzone w ciągu 14 dni.{{/if}}' } },
};

/** Klient zgłosił zmiany przed podpisem: umowa wraca do przygotowania jako nowa wersja (docs/10 §3.4). */
export const contractChangesSchema = z.object({ note: z.string().trim().min(3, 'Opisz, co klient chce zmienić').max(2000) });

// ── rozliczenia pozycji harmonogramu (docs/10 §3.5) ──

export const PAYMENT_KINDS = { zaliczka: 'Zaliczka', rata: 'Rata', koncowa: 'Płatność końcowa', inna: 'Inna' } as const;
export type PaymentKind = keyof typeof PAYMENT_KINDS;

/** Zdarzenie, od którego zależy czynność (zadanie dla księgowości). Nie przesądza o obowiązku podatkowym. */
export const PAYMENT_TRIGGERS = {
  podpis_umowy: 'Po podpisaniu umowy',
  zaplanowanie: 'Po zaplanowaniu prac',
  rozpoczecie_prac: 'Przy rozpoczęciu prac',
  /** wszystkie zadania wykonawcze (montaż / uruchomienie) zakresów umowy zakończone — bez czekania na protokół odbioru */
  zakonczenie_prac: 'Po zakończeniu prac (montażu)',
  odbior: 'Po odbiorze prac (protokół)',
  termin: 'W terminie płatności',
  reczny: 'Bez zadania (księgowość ręcznie)',
} as const;
export type PaymentTrigger = keyof typeof PAYMENT_TRIGGERS;

/** Domyślna liczba dni na zapłatę od zdarzenia (pozycja bez daty w umowie), gdy firma nie ustawiła własnej. */
export const DEFAULT_EVENT_PAYMENT_DAYS = 7;

/** Termin płatności od zdarzenia: data zdarzenia + N dni (docs/10 §3.5). */
export function eventDueDate(eventDate: string, days: number | null | undefined): string {
  const d = new Date(`${eventDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + (days ?? DEFAULT_EVENT_PAYMENT_DAYS));
  return d.toISOString().slice(0, 10);
}

/**
 * Pozycja harmonogramu, której nie da się wydać: „w terminie” bez daty (zadanie rozliczenia nigdy by nie powstało)
 * albo zaliczka warunkująca start bez terminu i bez zdarzenia. Zwraca komunikat dla użytkownika albo null.
 */
export function scheduleItemProblem(item: { name: string; dueDate: string | null; conditionsStart: boolean; trigger: PaymentTrigger }): string | null {
  if (item.dueDate) return null;
  if (item.trigger === 'termin') return `Płatność „${item.name}” ma być rozliczona „w terminie płatności”, ale nie ma daty — wpisz termin albo wybierz zdarzenie w polu „Kiedy rozliczyć”`;
  if (item.conditionsStart && item.trigger === 'reczny') return `Zaliczka „${item.name}” warunkuje rozpoczęcie prac i nie ma terminu — wpisz termin albo wybierz zdarzenie (np. „Po podpisaniu umowy”)`;
  return null;
}

/**
 * QA8 P16: płatności „po zakończeniu prac” i „po odbiorze” — montaż zwykle kończy się podpisem protokołu, więc obie stają się
 * wymagalne tego samego dnia i rozlicza je jedna faktura końcowa (np. 30/40/30 → w praktyce 30/70). Zwraca ostrzeżenie albo null.
 */
export function sameDayInstallmentsWarning(items: { name: string; trigger: PaymentTrigger }[]): string | null {
  const done = items.find((i) => i.trigger === 'zakonczenie_prac');
  const acc = items.find((i) => i.trigger === 'odbior');
  if (!done || !acc) return null;
  return `Płatności „${done.name}” (po zakończeniu prac) i „${acc.name}” (po odbiorze) wypadną tego samego dnia, gdy montaż kończy się podpisem protokołu — klient dostanie na obie jedną fakturę końcową. Jeśli rata ma być rozliczona osobno, wybierz dla niej wcześniejsze zdarzenie (np. „Przy rozpoczęciu prac”) albo „W terminie płatności” z datą; inna liczba „Dni od zdarzenia” zmienia tylko termin zapłaty.`;
}

/** Wymagany dokument rozliczeniowy pozycji — według przyjętego procesu firmy (do ustalenia z księgowością). */
export const SETTLEMENT_DOCUMENTS = {
  brak: 'Bez dokumentu',
  proforma: 'Faktura proforma',
  zaliczkowa: 'Faktura zaliczkowa (do każdej wpłaty)',
  koncowa: 'Faktura końcowa / VAT',
} as const;
export type SettlementDocument = keyof typeof SETTLEMENT_DOCUMENTS;

/**
 * P4: co dla klienta oznacza wybór dokumentu dla zaliczki (ustawienia firmy) — kiedy dostaje wezwanie do zapłaty.
 * Niezależnie od wyboru klient dostaje po podpisie e-mail „Harmonogram płatności” (kwoty, terminy, rachunek).
 */
export const DEPOSIT_DOCUMENT_HINTS: Record<SettlementDocument, string> = {
  brak: 'Bez faktury do zaliczki — klient płaci według harmonogramu z umowy (e-mail „Harmonogram płatności” po podpisie).',
  proforma: 'Przed wpłatą księgowość wystawia fakturę proforma — wezwanie do zapłaty z kwotą, terminem i rachunkiem, wysyłane klientowi e-mailem. Po wpłacie można wystawić fakturę zaliczkową.',
  zaliczkowa: 'Faktura zaliczkowa powstaje dopiero po wpłacie (dokumentuje wpłatę, nie wzywa do zapłaty). Przed wpłatą klient ma tylko harmonogram płatności z umowy i przypomnienia — jeśli chcesz wysyłać wezwanie z rachunkiem, wybierz „Faktura proforma”.',
  koncowa: 'Zaliczka rozliczana dopiero fakturą końcową / VAT — bez osobnego dokumentu przed wpłatą.',
};

export const paymentSettlementSchema = z.object({
  trigger: z.enum(Object.keys(PAYMENT_TRIGGERS) as [PaymentTrigger, ...PaymentTrigger[]]),
  documentRequired: z.enum(Object.keys(SETTLEMENT_DOCUMENTS) as [SettlementDocument, ...SettlementDocument[]]),
  requiredForClose: z.boolean(),
});
export type PaymentSettlementInput = z.infer<typeof paymentSettlementSchema>;

/** „po montażu”, „po wykonaniu (prac)”, „po zakończeniu prac / montażu” — zdarzenie: zakończenie prac. */
const WORKS_DONE = /po\s+(monta[żz]u|wykonaniu|zako[ńn]czeniu)|zako[ńn]czeni[ua]\s+(prac|monta)/i;
/** „przy rozpoczęciu prac”, „przed montażem”, „na start montażu” — zdarzenie: rozpoczęcie prac. */
const WORKS_START = /rozpocz[ęe]ci|przed\s+monta[żz]em|na\s+start/i;
/** „po odbiorze”, „płatność końcowa” — zdarzenie: odbiór (protokół). */
const ACCEPTANCE = /odbi|ko[ńn]cow/i;
/** „Zaliczka 30%”, „przedpłata”, „zadatek” — pozycja to zaliczka (P12). */
const DEPOSIT = /zaliczk|przedp[łl]at|zadat(ek|ku)/i;

/**
 * Domyślne ustawienia rozliczenia nowej pozycji — z ustawień firmy (zaliczka / płatność końcowa).
 * Zdarzenie rozpoznawane z nazwy („po montażu”, „po wykonaniu”, „przy rozpoczęciu prac”, „po odbiorze”);
 * bez wskazówki w nazwie — „w terminie” (wtedy umowę wydaje się dopiero z datą albo wybranym zdarzeniem).
 */
export function settlementDefaults(
  item: { name: string; conditionsStart: boolean },
  settings: { depositDocument?: SettlementDocument; finalDocument?: SettlementDocument },
): { kind: PaymentKind; trigger: PaymentTrigger; documentRequired: SettlementDocument; requiredForClose: boolean } {
  // P12: zaliczka także po nazwie („Zaliczka 30%”, „przedpłata”, „zadatek”) — bez zaznaczonego „warunku startu”
  if (item.conditionsStart || DEPOSIT.test(item.name)) return { kind: 'zaliczka', trigger: 'podpis_umowy', documentRequired: settings.depositDocument ?? 'zaliczkowa', requiredForClose: true };
  const final = /ko[ńn]cow/i.test(item.name);
  const finalDoc = settings.finalDocument ?? 'koncowa';
  if (WORKS_DONE.test(item.name)) return { kind: final ? 'koncowa' : 'rata', trigger: 'zakonczenie_prac', documentRequired: final ? finalDoc : 'brak', requiredForClose: true };
  if (ACCEPTANCE.test(item.name)) return { kind: 'koncowa', trigger: 'odbior', documentRequired: finalDoc, requiredForClose: true };
  if (WORKS_START.test(item.name)) return { kind: 'rata', trigger: 'rozpoczecie_prac', documentRequired: 'brak', requiredForClose: true };
  return { kind: 'rata', trigger: 'termin', documentRequired: 'brak', requiredForClose: true };
}
